import {
    Box, Container, Divider, FormControl, FormControlLabel, FormGroup, FormHelperText, Grid, Switch, Typography,
} from '@mui/material';
import {
    memo, useEffect, useRef, useState,
} from 'react';
import { useForm } from 'react-hook-form';
import { LoggedInUser, UserSettingsViewModel } from '../../../types/auth.types';
import { TagViewModel } from '../../../types/tags.types';
import { ShadowBGContainer } from '../foundation/Containers';
import Select from '../foundation/Select';
import RemovableTag from '../foundation/RemovableTag';
import CustomBR from '../foundation/CustomBR';
import TextField from '../foundation/TextField';
import Page from '../layout/base.page';
import { GeoAutocomplete, LocationOption, TagsSearchAutocomplete } from '../components/AutocompleteRemote';
import { postUserSettings } from '../../api/user.api';
import UserNotification, { UserNotificationProps } from '../components/UserNotification';
import UserNotificationSettings from '../components/UserNotificationSettings';
import { NotificationDto } from '../../../common/notifications.types';
import { deleteUserSearchKeys } from '../../api/stats.api';
import { Flexbox } from '../foundation/Flex';
import { EXPRESS_VALIDITY_TEXT } from '../../../common/expressConsent';

export type UserSettingsPageProps = {
    url: string
    user?: LoggedInUser
    userSettings: UserSettingsViewModel
    isUserRegistered?: boolean
    currentSubscribedTags?: TagViewModel[],
    userNotificationSettings?: Record<string, NotificationDto>
    lastSearchKeys?: { searchKeyId: number, text: string, tagIds: string, displays: string, tags: string }[]
}

interface IFormUserSettings {
    firstname: string
    showToEmployers: boolean
    smartAgentByActivity: boolean
    job_distance: number
    smartAgentNotificationType: number
}

const agentOptions = [
    { value: 1, text: 'ללא' },
    { value: 2, text: 'יומי - כל הבוקר' },
    { value: 3, text: 'שבועי - ראשון בבוקר' },
    { value: 4, text: 'מיידי - מספר פעמים ביום' },
];

const UserSettingsPage = (props: UserSettingsPageProps) => {
    const [notification, setNotification] = useState<UserNotificationProps>(null);
    const [subscribedTags, setSubscribedTags] = useState<TagViewModel[]>(props.currentSubscribedTags || []);
    const [lastSearchKeys, setLastSearchKeys] = useState<{ searchKeyId: number, text: string, tagIds: string, displays: string, tags: string }[]>(props.lastSearchKeys || []);
    const {
        register, formState: { errors }, watch, trigger,
    } = useForm<IFormUserSettings>({
        defaultValues: {
            smartAgentNotificationType: props.userSettings.smartAgentNotificationType || 1,
            smartAgentByActivity: props.userSettings.smartAgentByActivity || false,
            job_distance: props.userSettings.job_distance,
            showToEmployers: props.userSettings.showToEmployers === true,
        },
    });

    const isMounted = useRef(false);

    const location = useRef<LocationOption>({
        latitude: props.userSettings.latitude,
        longitude: props.userSettings.longitude,
        formatted_address: props.userSettings.formatted_address,
    });

    const updateUserSettings = async () => {
        const data = watch();
        // trigger form validation
        const isFormValid = await trigger();
        if (!isFormValid) return;

        await postUserSettings(
            { userid: props.user.userid },
            {
                ...data,
                smartAgentNotificationType: data.smartAgentNotificationType,
                ...(location.current && location.current.latitude ? {
                    latitude: location.current.latitude,
                    longitude: location.current.longitude,
                    formatted_address: location.current.formatted_address,
                } : {}),
                tags: subscribedTags.map(t => t.tagid),
            },
            true,
        );
    };
    const locationChange = (_ev, val) => {
        location.current = val;
        updateUserSettings();
    };

    useEffect(() => {
        const subscription = watch(() => {
            updateUserSettings();
        });

        return () => subscription.unsubscribe();
    }, [watch]);

    useEffect(() => {
        if (!isMounted.current) return;
        updateUserSettings();
    }, [subscribedTags]);

    const jobDistanceInput = register('job_distance', {
        required: true, max: 1000, min: 0, valueAsNumber: true,
    });
    const smartAgentNotificationTypeInput = register('smartAgentNotificationType', { required: true, valueAsNumber: true });
    const showToEmployersInput = register('showToEmployers');
    const smartAgentByActivityInput = register('smartAgentByActivity');

    const removeSearchKey = async (searchKeyId: number) => {
        await deleteUserSearchKeys({ userId: props.user.userid, searchKeyId });
        setLastSearchKeys(lastSearchKeys.filter(sk => sk.searchKeyId !== searchKeyId));
    };

    useEffect(() => {
        isMounted.current = true;
    }, []);

    return <Page
        showBreadCrumbs={false}
        url={props.url}
        user={props.user}
        pageHeaderTitle="Mploy - הגדרות משתמש">
        <Container>
            <CustomBR h={8} />

            {/* Notification Alert */}
            {notification && (
                <Box mb={4}>
                    <UserNotification blink {...notification} />
                </Box>
            )}

            {/* Location Settings Section */}
            <Box mb={3}>
                <ShadowBGContainer>
                    <Box p={3}>
                        <Flexbox alignItems="flex-start" gap={1.5} mb={1}>
                            <Typography variant="h4" fontSize="28px" lineHeight="1">
                                    📍
                            </Typography>
                            <Box>
                                <Typography color="secondary" fontWeight="bold" variant="h5" gutterBottom>
                                        הגדרת מיקום
                                </Typography>
                                <Typography color="textSecondary" variant="body2">
                                        קבע את מיקום החיפוש שלך ואת רדיוס המשרות המתאימות
                                </Typography>
                            </Box>
                        </Flexbox>

                        <CustomBR h={24} />

                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <Box mb={3}>
                                    <Typography color="secondary" variant="body2" fontWeight="600" mb={1}>
                                            מיקום מרכזי
                                    </Typography>
                                    <GeoAutocomplete
                                        defaultValue={props.userSettings.formatted_address}
                                        onChange={locationChange}
                                        helperText="בחר את המיקום ממנו נחפש לך משרות"
                                    />
                                </Box>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Box>
                                    <Typography color="secondary" variant="body2" fontWeight="600" mb={1}>
                                    רדיוס חיפוש
                                    </Typography>
                                    <Box flex={1}>
                                        <TextField
                                            defaultValue={props.userSettings.job_distance}
                                            required
                                            {...jobDistanceInput}
                                            inputRef={jobDistanceInput.ref}
                                            name="job_distance"
                                            id="job_distance"
                                            label="מרחק בק״מ"
                                            type="number"
                                            fullWidth
                                            error={!!errors.job_distance}
                                            helperText={errors.job_distance ? 'יש להזין מרחק תקין (0-1000)' : 'עד כמה ק"מ מהמיקום שלך לחפש'}
                                        />
                                    </Box>
                                </Box>
                            </Grid>
                        </Grid>
                    </Box>
                </ShadowBGContainer>
            </Box>

            {/* Smart Agent Section */}
            <Box mb={3}>
                <ShadowBGContainer>
                    <Box p={3}>
                        <Flexbox alignItems="flex-start" gap={1.5} mb={1}>
                            <Typography variant="h4" fontSize="28px" lineHeight="1">
                                    🤖
                            </Typography>
                            <Box>
                                <Typography color="secondary" fontWeight="bold" variant="h5" gutterBottom>
                                        סוכן חכם
                                </Typography>
                                <Typography color="textSecondary" variant="body2">
                                        קבל התראות אוטומטיות על משרות חדשות שמתאימות לך
                                </Typography>
                            </Box>
                        </Flexbox>

                        <CustomBR h={24} />

                        <Box bgcolor="grey.50" borderRadius={2} p={2.5} mb={3}>
                            <Typography color="primary" variant="body2" fontWeight="600" mb={1.5}>
                                    תדירות התראות
                            </Typography>
                            <FormControl fullWidth>
                                <Select
                                    size="medium"
                                    defaultValue={props.userSettings.smartAgentNotificationType}
                                    name="smartAgentNotificationType"
                                    id="smartAgentNotificationType"
                                    {...smartAgentNotificationTypeInput}
                                    inputRef={smartAgentNotificationTypeInput.ref}
                                    required
                                    fullWidth
                                    options={agentOptions}
                                />
                                <FormHelperText>כיצד תרצה לקבל עדכונים על משרות חדשות?</FormHelperText>
                            </FormControl>
                        </Box>

                        {watch('smartAgentNotificationType') !== 1 && (
                            <>
                                <Divider />
                                <CustomBR h={24} />

                                {/* Subscribed Tags Section */}
                                <Box mb={3}>
                                    <Flexbox alignItems="center" justifyContent="space-between" mb={2}>
                                        <Typography color="secondary" variant="h6" fontWeight="bold">
                                                תחומי מעקב
                                        </Typography>
                                        {subscribedTags && subscribedTags.length > 0 && (
                                            <Box bgcolor="info.light" borderRadius="12px" px={1.5} py={0.5}>
                                                <Typography color="info.dark" variant="caption" fontWeight="bold">
                                                    {subscribedTags.length} תפקידים
                                                </Typography>
                                            </Box>
                                        )}
                                    </Flexbox>

                                    <Typography color="textSecondary" variant="body2" mb={2}>
                                            התפקידים והתחומים שאתה מעוניין לעקוב אחריהם
                                    </Typography>

                                    {subscribedTags && subscribedTags.length > 0 ? (
                                        <Box bgcolor="grey.50" borderRadius={2} p={2} mb={2}>
                                            <Flexbox flexWrap="wrap" gap={1}>
                                                {subscribedTags.map(tag => (
                                                    <RemovableTag
                                                        key={tag.tagid}
                                                        text={tag.tag}
                                                        onRemoveClick={() => setSubscribedTags(subscribedTags.filter(t => t.tagid !== tag.tagid))}
                                                    />
                                                ))}
                                            </Flexbox>
                                        </Box>
                                    ) : (
                                        <Box
                                            bgcolor="warning.light"
                                            borderRadius={2}
                                            p={3}
                                            mb={2}
                                            border="2px dashed"
                                            borderColor="warning.main"z
                                        >
                                            <Flexbox flexDirection="column" alignItems="center" gap={1}>
                                                <Typography variant="h5">🎯</Typography>
                                                <Typography color="textPrimary" variant="body2" fontWeight="600" align="center">
                                                        טרם נבחרו תפקידים למעקב
                                                </Typography>
                                                <Typography color="textSecondary" variant="caption" align="center">
                                                        הוסף תחומים ותפקידים כדי להתחיל לקבל התראות מותאמות אישית
                                                </Typography>
                                            </Flexbox>
                                        </Box>
                                    )}

                                    <TagsSearchAutocomplete
                                        filterItems={subscribedTags?.map(t => t.tag)}
                                        clearOnChange
                                        label="חפש והוסף תחום או תפקיד"
                                        onChange={(_ev, val: TagViewModel) => {
                                            setSubscribedTags([...subscribedTags, val]);
                                        }}
                                    />
                                </Box>

                                {/* Activity-Based Learning Section */}
                                <Divider />
                                <CustomBR h={24} />
                                <Box>
                                    <Typography color="secondary" variant="h6" fontWeight="bold" gutterBottom>
                                        למידה חכמה מהפעילות שלך
                                    </Typography>
                                    <Typography color="textSecondary" variant="body2" mb={2}>
                                        אפשר למערכת ללמוד מהפעילות שלך ולשלוח לך משרות רלוונטיות נוספות
                                    </Typography>
                                    <Box borderRadius={2} p={2.5}>
                                        <FormGroup>
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        {...smartAgentByActivityInput}
                                                        defaultChecked={!!props.userSettings.smartAgentByActivity}
                                                        inputRef={smartAgentByActivityInput.ref}
                                                        name="smartAgentByActivity"
                                                        color="primary"
                                                        size="medium"
                                                    />
                                                }
                                                label={
                                                    <Box ml={1}>
                                                        <Typography color="secondary" variant="body1" fontWeight="600" gutterBottom>
                                                            🧠 שלח לי משרות מומלצות בהתאם לפעילות שלי
                                                        </Typography>
                                                        <Typography color="textSecondary" variant="body2">
                                                            המערכת תנתח את החיפושים שלך, המשרות שצפית בהן והמשרות ששלחת אליהן קורות חיים,
                                                            ותשלח לך המלצות מותאמות אישית למשרות דומות שעשויות לעניין אותך.
                                                        </Typography>
                                                    </Box>
                                                }
                                            />
                                        </FormGroup>
                                    </Box>

                                    {/* Search History Section */}
                                    {lastSearchKeys && lastSearchKeys.length > 0 && (
                                        <>
                                            <Divider />
                                            <CustomBR h={24} />
                                            <Box>
                                                <Flexbox alignItems="center" justifyContent="space-between" mb={2}>
                                                    <Typography color="secondary" variant="h6" fontWeight="bold">
                                                        היסטוריית חיפושים
                                                    </Typography>
                                                    <Box bgcolor="info.light" borderRadius="12px" px={1.5} py={0.5}>
                                                        <Typography color="info.dark" variant="caption" fontWeight="bold">
                                                            {lastSearchKeys.length} חיפושים
                                                        </Typography>
                                                    </Box>
                                                </Flexbox>
                                                <Typography color="textSecondary" variant="body2" mb={2}>
                                                    החיפושים האחרונים שביצעת במערכת
                                                </Typography>
                                                <Box bgcolor="grey.50" borderRadius={2} p={2}>
                                                    <Flexbox flexWrap="wrap" gap={1}>
                                                        {lastSearchKeys.map(lastSearchKey => (
                                                            <RemovableTag
                                                                key={lastSearchKey.searchKeyId}
                                                                text={lastSearchKey.tags ? lastSearchKey.tags + (lastSearchKey.text || '') : (lastSearchKey.text || '')}
                                                                onRemoveClick={() => removeSearchKey(lastSearchKey.searchKeyId)}
                                                            />
                                                        ))}
                                                    </Flexbox>
                                                </Box>
                                            </Box>
                                        </>
                                    )}
                                </Box>
                            </>
                        )}
                    </Box>
                </ShadowBGContainer>
            </Box>

            {/* Notification Settings Section */}
            <Box mb={3}>
                <UserNotificationSettings
                    userId={props.user.userid}
                    activeNotificationSettings={props.userNotificationSettings}
                    setNotification={setNotification}
                />
            </Box>

            {/* Privacy Settings Section */}
            <Box mb={3}>
                <ShadowBGContainer>
                    <Box p={3}>
                        <Flexbox alignItems="flex-start" gap={1.5} mb={1}>
                            <Typography variant="h4" fontSize="28px" lineHeight="1">
                                    🔒
                            </Typography>
                            <Box>
                                <Typography color="secondary" fontWeight="bold" variant="h5" gutterBottom>
                                        פרטיות ונראות
                                </Typography>
                                <Typography color="textSecondary" variant="body2">
                                        שלוט במי יכול לראות את הפרופיל שלך
                                </Typography>
                            </Box>
                        </Flexbox>

                        <CustomBR h={24} />

                        <Box bgcolor="grey.50" borderRadius={2} p={2.5}>
                            <FormGroup>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            {...showToEmployersInput}
                                            defaultChecked={!!props.userSettings.showToEmployers}
                                            inputRef={showToEmployersInput.ref}
                                            name="showToEmployers"
                                            color="primary"
                                            size="medium"
                                        />
                                    }
                                    label={
                                        <Box ml={1}>
                                            <Typography color="secondary" variant="body1" fontWeight="600" gutterBottom>
                                                    Mploy Express – הפצת קורות החיים למעסיקים
                                            </Typography>
                                            <Typography color="textSecondary" variant="body2">
                                                    עם Mploy Express נוכל להמליץ עליך למעסיקים מתאימים ולהעביר להם את קורות החיים שלך
                                                <br />
                                                    הפעלת השירות מהווה אישור ל־Mploy לשתף את קורות החיים שלך עם מעסיקים
                                            </Typography>
                                            <Typography color="textSecondary" variant="body2" mt={1}>
                                                {EXPRESS_VALIDITY_TEXT}
                                            </Typography>
                                        </Box>
                                    }
                                />
                            </FormGroup>
                        </Box>
                    </Box>
                </ShadowBGContainer>
            </Box>

            <CustomBR h={32} />
        </Container>
    </Page>;
};

export default memo(UserSettingsPage);
